import { sharedSiteSettingsFields } from "@contentful-api/constants/queryFields";
import { deepClean } from "@contentful-api/helpers/deepClean";
import type { IMicrocopySetFields, ISiteSettingsFields } from "@contentful-api/types/contentful";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { CMSWrapper } from "@lib/wrappers/cmsWrapper";
import type { Entry } from "contentful";

export interface SiteSettingsContentProps {
    locale: string;
    localeTagId: string;
    pageType: string;
    selectedFields?: string[];
    microcopySetKey?: string;
}

interface IGetSiteSettingsResponse {
    content: Entry<ISiteSettingsFields> | null;
    sharedMicrocopySet: Entry<IMicrocopySetFields> | null;
    pageMicrocopySet: Entry<IMicrocopySetFields> | null;
}

export const getSiteSettingsContent = async ({
    locale,
    localeTagId,
    pageType,
    selectedFields = [],
    microcopySetKey,
}: SiteSettingsContentProps): Promise<IGetSiteSettingsResponse> => {
    const fieldsToSelect = [...sharedSiteSettingsFields, ...selectedFields];

    const startTime = performance.now();
    const { items, includes } = await CMSWrapper.getInstance().getEntries<ISiteSettingsFields>({
        content_type: "siteSettings",
        "metadata.tags.sys.id[in]": localeTagId,
        select: fieldsToSelect?.join(","),
        include: 10,
        limit: 1,
        locale,
    });
    Logger.info(ServiceType.CONTENTFUL, "Site Settings Contentful Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType,
    });

    const textCollectionEntries = (includes?.Entry as Entry<IMicrocopySetFields>[]) ?? [];

    return deepClean({
        content: items[0] ?? null,
        sharedMicrocopySet:
            textCollectionEntries.find((entry) => entry.fields.key === "shared") ?? null,
        pageMicrocopySet: microcopySetKey
            ? (textCollectionEntries.find((entry) => entry.fields.key === microcopySetKey) ?? null)
            : null,
    }) as IGetSiteSettingsResponse;
};
